import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import SelectionButton from 'modules/selection-button/index.js';
import PriceBoostBanner from 'modules/price-boost-banner/index.js';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';
import { useEventItem, useMarketItems, useEventsLoadDone } from 'modules/common/hooks';
import MarketHelper from 'modules/market/helper/market-helper';
import CardBackground from 'modules/event-cards/background/index.js';
import EventCardsTypes from 'modules/event-cards/types.js';
import SelectionCardTypes from 'modules/event-cards/selection/types.js';
import { useSelectionCardClick } from 'modules/event-cards/selection/hooks/index.js';

const {
    FlexPane,
    Text,
    Button,
    CompetitorIcon,
    SportAndCountryIcon,
    ParticipantItem,
    EventTimingStatus,
    competitorIconHooks,
    scoreboardUtils: { sortParticipants },
} = MojitoPresentation.Components;
const { useCompetitorsIconsQuery } = competitorIconHooks;
const { EventFormatter } = MojitoPresentation.Utils;
const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const { PARTICIPANT_POSITION } = MojitoServices.SportsContent.Events.types;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;

const useHasMoreThanOneLine = lineHeight => {
    const ref = useRef();
    const [hasMoreThanOneLine, setHasMoreThanOneLine] = useState(false);
    useLayoutEffect(() => {
        const clientHeight = ref?.current?.clientHeight;
        setHasMoreThanOneLine(clientHeight > lineHeight);
    }, [ref?.current?.clientHeight, lineHeight]);
    return [ref, hasMoreThanOneLine];
};

const getSelectionNameHeight = (selectionNameConfig = {}) => {
    const oneLineHeightPx =
        selectionNameConfig.style?.lineHeight || selectionNameConfig.style?.fontSize;
    return (oneLineHeightPx && oneLineHeightPx.match(/\d+/)) || 0; // 0 will make market name truncate on one line.
};

const getMarketNameConfig = (hasPromotionText, hasMoreThanOneLine, marketName) => {
    if (hasPromotionText) {
        return marketName.withPromotionText;
    }
    if (!hasMoreThanOneLine) {
        return marketName.default;
    }
    return marketName.oneRowTruncation;
};

const CardHeader = props => {
    const { event, mojitoTools, isBoosted, contentConfig } = props;
    const { config, appContext, stringResolver } = mojitoTools;

    const altText =
        appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');

    const isOutright = EventUtils.isOutrightEventType(event.eventType);
    const isRacing = EventUtils.isRaceEventType(event.eventType);
    const titleConfig = isBoosted
        ? contentConfig.boostedEventCard.title
        : contentConfig.genericEventCard.title;

    return (
        <FlexPane class={'ta-headerContainer'} config={config.headerContainer}>
            <SportAndCountryIcon
                sportId={event.sportId}
                countryCode={event.countryCode}
                countryName={event.countryName}
                alt={altText}
                config={contentConfig.sportCountryIcons}
            />
            {isOutright || isRacing ? (
                <Text class={'ta-eventName'} config={titleConfig}>
                    {`${EventFormatter.formatEventName(event.name)}`}
                </Text>
            ) : (
                <Participants {...props} />
            )}
            {!isOutright && <EventStatusInfo {...props} />}
        </FlexPane>
    );
};

const Participants = props => {
    const { event, mojitoTools, contentConfig } = props;
    const { config, stringResolver } = mojitoTools;

    const participants = useMemo(() => {
        return sortParticipants(event.participants, event.homeAway).map(p => ({
            ...p,
            sportId: event.sportId,
        }));
    }, [event?.homeAway, event?.participants, event?.sportId]);

    const { competitorsWithIcons } = useCompetitorsIconsQuery({
        competitors: participants,
        config: config.competitorIconOptions,
    });

    if (!event.participants) {
        return null;
    }

    const separatorLabel = stringResolver.resolveString('$SELECTION_CARD.SEPARATOR_LABEL');

    if (EventUtils.isThreeParticipantsEvent(event)) {
        const participantNames = competitorsWithIcons.map(p => p.name).join(` ${separatorLabel} `);
        return <Text config={contentConfig.threeParticipantText}>{participantNames}</Text>;
    }

    const [competitorOne = {}, competitorTwo = {}] = competitorsWithIcons;

    return (
        <>
            <ParticipantItem
                sportId={event.sportId}
                participant={competitorOne}
                config={contentConfig.reversedParticipantItem}
            />
            <Text config={contentConfig.separatorLabel}>{separatorLabel}</Text>
            <ParticipantItem
                sportId={event.sportId}
                participant={competitorTwo}
                config={contentConfig.participantItem}
            />
        </>
    );
};

const EventStatusInfo = props => {
    const { event, mojitoTools, isBoosted, contentConfig } = props;
    const { stringResolver } = mojitoTools;
    const shouldShowLiveLabel = !isBoosted && event.details.inPlay;

    return shouldShowLiveLabel ? (
        <Text config={contentConfig.headerLiveLabel}>
            {stringResolver.resolveString('$SELECTION_CARD.LIVE')}
        </Text>
    ) : (
        <EventTimingStatus
            config={contentConfig.headerTime}
            eventItem={event}
            hasStreaming={VideoStreamsUtils.isStreamAvailable(event)}
            hasClockDetails={EventUtils.hasInPlayClockDetails(event)}
            inplayTime={EventUtils.getEventInplayTime(event)}
        />
    );
};

const CardBody = props => {
    const {
        event,
        selectionName,
        handicapLabel,
        marketName,
        hasPromotionText,
        promotionText,
        mojitoTools,
        selection = {},
        contentConfig,
    } = props;
    const { config } = mojitoTools;

    const [selectionNameRef, hasMoreThanOneLine] = useHasMoreThanOneLine(
        Number(getSelectionNameHeight(contentConfig.selectionName.default))
    );

    const competitors = useMemo(
        () =>
            event.participants
                ? event.participants.map(p => ({ ...p, sportId: event.sportId }))
                : [],
        [event.participants, event.sportId]
    );

    const { competitorsWithIcons } = useCompetitorsIconsQuery({
        competitors,
        config: config.competitorIconOptions,
    });

    if (!event.participants) {
        return null;
    }

    const { AWAY, HOME } = PARTICIPANT_POSITION;
    let position;
    if (MarketHelper.isHomeTeam(selection)) {
        position = HOME;
    } else if (MarketHelper.isAwayTeam(selection)) {
        position = AWAY;
    }

    const participantIndex = position
        ? competitorsWithIcons.findIndex(competitor => competitor.position === position)
        : -1;
    const title = competitorsWithIcons[participantIndex]?.name;
    const iconSrc = competitorsWithIcons[participantIndex]?.iconSrc;

    const marketNameConfig = getMarketNameConfig(
        hasPromotionText,
        hasMoreThanOneLine,
        contentConfig.marketName
    );

    return (
        <FlexPane config={config.selectionBody}>
            <CompetitorIcon title={title} src={iconSrc} config={config.competitorIconBody} />
            <FlexPane config={config.selectionInfo} class="ta-selectionInfo">
                {hasPromotionText && (
                    <Text config={contentConfig.promotionText}>{promotionText}</Text>
                )}
                <Text
                    config={
                        hasPromotionText
                            ? contentConfig.selectionName.withPromotionText
                            : contentConfig.selectionName.default
                    }
                    textRef={selectionNameRef}
                >
                    {`${selectionName} ${handicapLabel}`}
                </Text>
                <Text config={marketNameConfig}>{marketName}</Text>
            </FlexPane>
        </FlexPane>
    );
};

const SelectionCardClassic = withEventCardSkeleton(
    props => {
        const {
            eventId,
            marketId,
            selectionId,
            promotionText,
            mojitoTools,
            colorMode,
            shouldRequestData,
            elementRef,
        } = props;
        const { config, style, appContext, l10n } = mojitoTools;
        const mode = colorMode || config.mode;
        const contentConfig =
            mode === EventCardsTypes.COLOR_MODES.DARK
                ? style.eventCardDetailsDark
                : style.eventCardDetails;
        const event = useEventItem(eventId, shouldRequestData);
        const market = useMarketItems([marketId], shouldRequestData)[0] || {};
        const { oddsFormat } = appContext.userSettings();
        const selection = EventUtils.getSelectionFromMarket(market, selectionId);
        const handicapLabel = selection?.handicapLabel ? ` ${selection?.handicapLabel}` : '';
        const basePrice =
            selection?.basePrice && EventUtils.getOdds(selection.basePrice, oddsFormat, l10n);
        const onEventClick = useSelectionCardClick(event, appContext);

        if (!event) {
            return null;
        }

        const shouldShowLiveLabel = !basePrice && event.details.inPlay;

        const cardConfig = basePrice
            ? contentConfig.boostedEventCard
            : contentConfig.genericEventCard;
        const showBackground =
            props.imgUrl ||
            (config.showBackgroundSportImage &&
                !config.noBackgroundImageSports.includes(event.sportId));
        const hasPromotionText = Boolean(promotionText);

        return (
            <FlexPane
                class="ta-SelectionCard"
                config={
                    basePrice
                        ? contentConfig.boostedEventCard.container
                        : contentConfig.genericEventCard.container
                }
                elementRef={elementRef}
            >
                {showBackground && (
                    <CardBackground
                        config={config.cardBackground}
                        sportId={event.sportId}
                        imgUrl={props.imgUrl}
                    />
                )}
                <Button onClick={onEventClick} config={cardConfig.clickableArea}>
                    <CardHeader
                        {...props}
                        contentConfig={contentConfig}
                        event={event}
                        shouldShowLiveLabel={shouldShowLiveLabel}
                        isBoosted={Boolean(basePrice)}
                    />
                    <CardBody
                        {...props}
                        contentConfig={contentConfig}
                        event={event}
                        selection={selection}
                        handicapLabel={handicapLabel}
                        hasPromotionText={hasPromotionText}
                    />
                </Button>
                {basePrice ? (
                    <PriceBoostBanner
                        config={config.priceBoostBanner}
                        event={event}
                        market={market}
                        selection={selection}
                        basePrice={basePrice}
                    />
                ) : (
                    <FlexPane config={config.selectionContainer}>
                        <SelectionButton
                            config={contentConfig.selectionButton}
                            selection={selection || SelectionCardTypes.BOGUS_SELECTION}
                            event={event}
                            marketStatus={market.status}
                            marketType={market.type}
                            marketId={market.id}
                        />
                    </FlexPane>
                )}
            </FlexPane>
        );
    },
    props => useEventsLoadDone([props.eventId]),
    config => config.eventCardDetails?.genericEventCard?.skeletonLoader
);

SelectionCardClassic.getStyle = (config, mode, merge) => {
    const buildDetails = (details = {}) => {
        const participantItem = details.participantItem || {};
        const marketName = details.marketName || {};
        const reversedParticipantItem = merge(participantItem, {
            participantWrapper: { style: { flexDirection: 'row-reverse' } },
        });
        const marketNameOneRowTruncation = merge(marketName.default || {}, {
            ellipsisOnLineNumber: 1,
        });
        const promotionText = merge(details.promotionText || {}, { ellipsisOnLineNumber: 1 });

        return merge(details, {
            // Second participant is rendered mirrored so the competitor icon sits on the inner edge.
            reversedParticipantItem,
            promotionText,
            marketName: {
                oneRowTruncation: marketNameOneRowTruncation,
            },
        });
    };
    const eventCardDetails = buildDetails(config.eventCardDetails);
    const eventCardDetailsDark = buildDetails(
        merge(config.eventCardDetails || {}, config.eventCardDetailsDark || {})
    );

    return {
        eventCardDetails,
        eventCardDetailsDark,
    };
};

export default SelectionCardClassic;
